// useScoreCounter.ts
import {callFetch} from '@/components/helpers/utils';
import {useEffect, useRef, useState} from 'react';

interface ScoreCounterOptions {
  initialValue?: number; // initial score
  duration?: number; // animation time（ms）, default 1500
  pollInterval?: number; // polling API interval（ms），default 10000
  apiEndpoint?: string; // API path
  format?: 'comma' | 'none'; // format comma
}

export const ScoreCounter = ({
  initialValue = 1801293,
  duration = 1500,
  pollInterval = 10000,
  apiEndpoint = '/api/smile/score',
  format = 'comma'
}: ScoreCounterOptions = {}) => {
  const [score, setScore] = useState<number>(initialValue);
  const [isInitializing, setIsInitializing] = useState(true);
  const [displayScore, setDisplayScore] = useState<string>(
    initialValue.toLocaleString('en-US')
  );
  const currentTargetRef = useRef<number>(initialValue);
  const animationRef = useRef<number | null>(null);
  const elementRef = useRef<HTMLDivElement>(null);

  // format number
  const formatNumber = (num: number): string => {
    const str = num.toLocaleString('en-US');
    return format === 'comma' ? str : num.toString();
  };

  // animate to target score
  const animateTo = (target: number) => {
    if (!elementRef.current) return;

    const start = currentTargetRef.current;
    const difference = target - start;
    const steps = Math.max(30, Math.floor(duration / 16)); //  60fps
    const increment = difference / steps;
    let currentStep = 0;

    const step = () => {
      currentStep++;
      const newValue = Math.round(start + increment * currentStep);
      setDisplayScore(formatNumber(newValue));

      if (currentStep < steps) {
        animationRef.current = requestAnimationFrame(step);
      } else {
        setDisplayScore(formatNumber(target));
        currentTargetRef.current = target;
        animationRef.current = null;
      }
    };

    // cancel previous animation
    if (animationRef.current) cancelAnimationFrame(animationRef.current);

    // trigger floating animation class
    elementRef.current.classList.add('floating');

    step();

    // remove floating class
    setTimeout(() => {
      elementRef.current?.classList.remove('floating');
    }, duration + 300);
  };

  // get latest score
  const fetchScore = async () => {
    // if not current tab, return
    if (document.hidden) {
      return;
    }

    try {
      const res = await callFetch(apiEndpoint);

      if (!res.ok) return;

      const result = await res.json();
      const newScore = result?.data?.total_score ?? 0;

      if (newScore !== currentTargetRef.current) {
        setScore(newScore);
        animateTo(newScore);
      }
    } catch (error) {
      console.error('Fetch score error:', error);
    }
  };

  // Polling
  useEffect(() => {
    fetchScore();
    setIsInitializing(false);
    if (pollInterval > 0) {
      const interval = setInterval(fetchScore, pollInterval);
      return () => clearInterval(interval);
    }
  }, [pollInterval, apiEndpoint]);

  useEffect(() => {
    if (score !== initialValue) {
      setTimeout(() => animateTo(score));
    }
  }, [score]);

  return {
    displayScore,
    score,
    ref: elementRef
  };
};
